import React, { useContext, useState, useEffect } from 'react'
import styled from 'styled-components'
import { Form, Field } from 'react-final-form'
import moment from 'moment'
import { find, map } from 'lodash/fp'
import { useDebounce } from '../../utils/misc'
import withPermissions from '../../containers/withPermissions'
import TextInput from '../../components/TextInput/index'
import { flexBoxStyles } from '../../components/Flexbox'
import Button from '../../components/Button'
import DatePicker from '../../components/DatePicker/DatePicker'
import Select from '../../components/Select/index'
import CloseIcon from '../../images/closeIcon.svg'
import { AppContext } from '../App'
import { filterOptions } from './constants'
import { USERS_ENDPOINT } from '../Users/UserList'
import { tasks } from '../ReportsGen/utils'

const decorate = withPermissions

const numberRegex = /[^0-9]/g
const smallFieldWidth = 150
const fieldWidth = 310
const genderOptions = [
  { value: 'Male', label: 'Męska' },
  { value: 'Female', label: 'Żeńska' },
]

const makeOption = (option) => ({
  label: `${option.firstName} ${option.lastName}`,
  value: option.id,
})

const LoginForm = ({ onSubmit, onClose, hasRoles, initialValues }) => {
  const { withAuth } = useContext(AppContext)
  const [inputValue, setInputValue] = useState('')
  const [diagnosticians, setDiagnosticians] = useState([])
  const [tempDiagnosticians] = useState(() => new Map())
  const [isLoading, setIsLoading] = useState(false)

  const fetchDiagnosticians = async () => {
    setIsLoading(true)
    if (hasRoles(['platformAdmin']) && inputValue.length >= 3) {
      try {
        const response = await withAuth(USERS_ENDPOINT, {
          method: 'POST',
          payload: {
            meta: { limit: 100, page: 0 },
            filters: {
              search: debouncedInputValue,
              roleName: 'diagnostician',
            },
          },
        })
        const diagnosticians = response.models
        const options = map(makeOption, diagnosticians)
        setDiagnosticians(options)
      } catch (e) {}
    } else if (inputValue.length < 3) {
      setDiagnosticians([])
    }
    setIsLoading(false)
  }

  const debouncedInputValue = useDebounce(inputValue, 300)
  const formatNumbers = (value) =>
    value === undefined ? '' : value.replace(numberRegex, '')

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => void fetchDiagnosticians(), [debouncedInputValue])

  const handleChange = (ids) => {
    if (ids.length === 0) {
      tempDiagnosticians.clear()
    }
    ids.map((id) => {
      if (!tempDiagnosticians.has(id)) {
        const diagnosticianToAdd = find(
          (diagnostician) => diagnostician.value === id,
          diagnosticians
        )
        tempDiagnosticians.set(id, diagnosticianToAdd)
      }
      Array.from(tempDiagnosticians.values())
        .filter((diagnostician) => !ids.includes(diagnostician.value))
        .map((diagnostician) => tempDiagnosticians.delete(diagnostician.value))
    })
    setDiagnosticians([])
  }

  return (
    <Form
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values) => onSubmit(values)}
    >
      {({ handleSubmit, invalid, form }) => (
        <FormWrapper>
          <Header justify="space-between" align="flex-start">
            Filtruj
            <CloseButton
              onClick={onClose}
              icon={CloseIcon}
              altTag=""
              color="plain"
              ariaLabel="Zamknij"
            />
          </Header>
          <Field
            component={TextInput}
            width={smallFieldWidth}
            label="Numer od"
            type="text"
            name="minId"
            format={formatNumbers}
            formatOnBlur
            initialValue=""
          />
          <Field
            component={TextInput}
            width={smallFieldWidth}
            label="Numer do"
            type="text"
            name="maxId"
            format={formatNumbers}
            formatOnBlur
            initialValue=""
          />
          <Field
            name="minDateOfBirth"
            component={DatePicker}
            width={smallFieldWidth}
            label="Data ur. od"
            maxDate={moment()}
            showYearDropdown
            showMonthDropdown
          />
          <Field
            name="maxDateOfBirth"
            component={DatePicker}
            width={smallFieldWidth}
            label="Data ur. do"
            popperPlacement="bottom-end"
          />
          <Field
            isClearable
            name="gender"
            component={Select}
            width={fieldWidth}
            label="Płeć"
            options={genderOptions}
            aria-label={"Płeć"}
          />
          <Field
            name="requiredTests"
            component={Select}
            width={fieldWidth}
            label="Wykonane testy"
            options={tasks}
            isMulti
            aria-label={"Wykonane testy"}
          />
          {hasRoles(['platformAdmin']) && (
            <Field name="diagnosticians">
              {(props) => (
                <Select
                  isClearable
                  component={Select}
                  width={fieldWidth}
                  label="Diagności"
                  aria-label={"Diagności"}
                  onInputChange={(val) => setInputValue(val)} // must be delta func
                  // to prevent from passing second argument to setState
                  options={[...diagnosticians, ...tempDiagnosticians.values()]}
                  isMulti
                  filterOption={filterOptions}
                  placeholder="Wpisz przynajmniej trzy znaki i wybierz"
                  isLoading={isLoading}
                  meta={props.meta}
                  input={{
                    ...props.input,
                    onChange: (ids) => {
                      handleChange(ids)
                      return props.input.onChange(ids)
                    },
                  }}
                />
              )}
            </Field>
          )}
          <StyledButton
            width={`${fieldWidth}px`}
            label="Filtruj"
            onClick={handleSubmit}
            disabled={invalid}
          />
          <StyledButton
            color="white"
            width={`${fieldWidth}px`}
            label="Reset"
            onClick={() => {
              form.reset()
              onSubmit({ search: initialValues.search })
            }}
          />
        </FormWrapper>
      )}
    </Form>
  )
}
const Header = styled.header`
  height: 100%;
  width: 100%;
  padding: 5px 0 0 0;
  ${flexBoxStyles}
`
const FormWrapper = styled.div`
  padding: 0 10px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: 85px;
  align-items: center;
  justify-items: center;
  height: 100vh;
  > header {
    grid-column: 1 / 3;
  }
  > button {
    grid-column: 1 / 3;
  }
  > div {
    &:nth-child(2) {
      grid-column: 1 / 2;
    }
    &:nth-child(3) {
      grid-column: 2 / 3;
    }
    &:nth-child(4) {
      grid-column: 1 / 2;
    }
    &:nth-child(5) {
      grid-column: 2 / 3;
    }
    &:nth-child(6) {
      grid-column: 1 / 3;
    }
    &:nth-child(7) {
      grid-column: 1 / 3;
    }
    &:nth-child(8) {
      grid-column: 1 / 3;
    }
  }
`
const StyledButton = styled(Button)`
  opacity: ${(props) => (props.disabled ? '0.5' : '1')};
`

const CloseButton = styled(Button)`
  padding: 0;
  margin: 0;
  width: 32px;
  height: 32px;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    position: static;
    margin-left: auto;
  }
`;

export default decorate(LoginForm)
